import { DateTimeUtil } from '../../common/DateTimeUtil';
import * as InternalTypes from '../../common/InternalTypes';
import { LoggerTexts } from '../../common/LoggerTexts';
import { Util } from '../../common/Util';
import { InstantPushConfigState } from '../../data/InstantPushConfigState';

export class InstantPushConfig {
	private _areaId: string;
	private _triggerName: string;
	private _validFrom: number;
	private _validTo: number;
	private _isEnabled: boolean;
	private _quietPeriodInHrs: number;
	private _maxLimit: number;
	private _showInvitation: boolean | undefined;

	init(
		areaId: string,
		triggerName: string,
		validFrom: string,
		validTo: string,
		isEnabled: boolean,
		quietPeriodInHrs: number,
		maxLimit: number,
		showInvitation?: boolean
	) {
		if (Util.stringIsEmpty(areaId)) {
			throw Error(LoggerTexts.INSTANT_PUSH_CFG.AREAID_CANNOT_BE_EMTPY);
		}
		this._areaId = areaId;
		if (Util.stringIsEmpty(triggerName)) {
			throw Error(LoggerTexts.INSTANT_PUSH_CFG.TRIGGERNAME_CANNOT_BE_EMTPY);
		}
		this._triggerName = triggerName;
		if (!DateTimeUtil.isDateValidInISO(validFrom)) {
			throw Error(`Valid-From(${validFrom}) is not a valid date for ${areaId} ${triggerName}`);
		}
		if (!DateTimeUtil.isDateValidInISO(validTo)) {
			throw Error(`Valid-To(${validTo}) is not a valid date for ${areaId} ${triggerName}`);
		}
		const formattedValidFrom = DateTimeUtil.getTimeFromISODate(validFrom);
		const formattedValidTo = DateTimeUtil.getTimeFromISODate(validTo);
		if (formattedValidFrom >= formattedValidTo) {
			throw Error(LoggerTexts.APP_PUSH_CFG.VALID_FROM_CANNOT_BE_AFTER_VALID_TO);
		}
		this._validFrom = formattedValidFrom;
		this._validTo = formattedValidTo;
		this._isEnabled = isEnabled;
		this._quietPeriodInHrs = quietPeriodInHrs;
		this._maxLimit = maxLimit;
		this._showInvitation = showInvitation;
	}

	initFromJsonObject(instantPushConfig: InternalTypes.InstantPushConfig) {
		const { areaId, triggerName, validFrom, validTo, isEnabled, quietPeriodInHrs, maxLimit, showInvitation } = instantPushConfig;
		this.init(areaId, triggerName, validFrom, validTo, isEnabled, quietPeriodInHrs, maxLimit, showInvitation);
	}

	get combinedKey(): string {
		return Util.getCombinedKey(this._areaId, this._triggerName);
	}

	get areaId(): string {
		return this._areaId;
	}

	get triggerName(): string {
		return this._triggerName;
	}

	get validFrom(): number {
		return this._validFrom;
	}

	get validTo(): number {
		return this._validTo;
	}

	get isEnabled(): boolean {
		return this._isEnabled;
	}

	get quietPeriodInHrs(): number {
		return this._quietPeriodInHrs;
	}

	get maxLimit(): number {
		return this._maxLimit;
	}

	get showInvitation(): boolean | undefined {
		return this._showInvitation;
	}

	isActive(): boolean {
		if (this.isEnabled && !this.isExpired()) {
			return true;
		}
		return false;
	}

	isExpired(): boolean {
		const now = Date.now();
		if (this._isValidNow(now) || this._isValidInFuture(now)) {
			return false;
		}
		return true;
	}

	isQualifiedForPush(instantPushConfigState: InstantPushConfigState): boolean {
		return instantPushConfigState.isQualifiedForPush(this);
	}

	private _isValidNow(timeToCompare: number): boolean {
		if (this.validFrom < timeToCompare && this.validTo > timeToCompare) {
			return true;
		}
		return false;
	}

	private _isValidInFuture(timeToCompare: number): boolean {
		if (this.validFrom > timeToCompare && this.validTo > timeToCompare && this.validTo > this.validFrom) {
			return true;
		}
		return false;
	}
}
